import { useEffect, useState } from "react";
import {
    MdLocalOffer,
    MdFilterList,
    MdCheckCircle,
    MdMenuBook,
    MdLibraryBooks,
    MdPhone,
} from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { Offer } from "../../../models/offers";
import OfferCreateForm from "./CreateOffer";
import { Filters, OfferFilters } from "./OfferFilterList";
import { OfferCardAll } from "./OfferCardAll";
import {
    getAllOffers,
    getMyOffers,
    getMySupply,
    getSupply,
} from "../../../api/offers";
import { getAllSaves, createSave, deleteSave } from "../../../api/savedOffers";
import { getAllApplications } from "../../../api/offerApplications";
import { useMarketplaceStore } from "../../../store/MarketPlaceStore";
import FilterBar from "./FilterBar";
import toast from "react-hot-toast";

type MarketplaceProps = {
    mode: "user" | "admin";
};

export default function Marketplace({ mode }: MarketplaceProps) {
    const {
        page,
        setPage,
        pageSize,
        currentParams,
        setCurrentParams,
        filteredOffers,
        setFilteredOffers,
        activeTab,
        setActiveTab,
        loading,
        setLoading,
        err,
        setErr,
        showFilters,
        setShowFilters,
        savedOfferIds,
        setSavedOfferIds,
        createFormOpen,
        setCreateFormOpen,
        currentFilters,
        setCurrentFilters,
        selectedVertical,
        setSelectedVertical,
        searchText,
        setSearchText

    } = useMarketplaceStore();
    const [debouncedSearch, setDebouncedSearch] = useState(searchText);
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchText);
        }, 400);
        return () => clearTimeout(handler);
    }, [searchText]);

    useEffect(() => {
        setCurrentParams({
            ...currentParams,
            page: 1,
            category: debouncedSearch || undefined,
        });
    }, [debouncedSearch]);

    const navigate = useNavigate();

    const TABS =
        mode === "user"
            ? [
                { key: "all", label: "All Offers", icon: <MdMenuBook size={25} className="mr-2" /> },
                { key: "saved", label: "Saved Offers", icon: <MdCheckCircle size={25} className="mr-2" /> },
                { key: "applied", label: "Applied Offers", icon: <MdLibraryBooks size={25} className="mr-2" /> },
                { key: "supply", label: "My Supply", icon: <MdPhone size={25} className="mr-2" /> },
            ]
            : [
                { key: "all", label: "Offers", icon: <MdMenuBook size={25} className="mr-2" /> },
                { key: "myOffers", label: "My Offers", icon: <MdLibraryBooks size={25} className="mr-2" /> },
                { key: "supply", label: "Other Supply", icon: <MdPhone size={25} className="mr-2" /> },
            ];

    useEffect(() => {
        if (mode === "user") {
            const loadSaved = async () => {
                try {
                    const saved = await getAllSaves();
                    setSavedOfferIds(saved.map((o: { id: number }) => o.id));
                } catch (_) { }
            };
            loadSaved();
        }
    }, [mode]);

    const fetchOffers = async (params?: Record<string, any>) => {
        setLoading(true);
        setErr("");
        try {
            let raw: Offer[] = [];
            const p = { ...params, page, page_size: pageSize };

            if (mode === "user") {
                if (activeTab === "saved") raw = await getAllSaves(p);
                else if (activeTab === "applied") raw = await getAllApplications(p);
                else if (activeTab === "supply") raw = await getMySupply(p);
                else raw = await getAllOffers(p);
            } else {
                if (activeTab === "myOffers") raw = await getMyOffers(p);
                else if (activeTab === "supply") raw = await getSupply(p);
                else raw = await getAllOffers(p);
            }

            setFilteredOffers(raw);
        } catch (e: any) {
            setErr(e?.message || "Failed to load offers.");
            setFilteredOffers([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOffers(currentParams);
    }, [currentParams]);

    useEffect(() => {
        setPage(1);
        setCurrentFilters({
            verticals: [],
            payoutRange: { min: "", max: "" },
            state_list: [],
            payout_type: "",
            campaign_types: []
        });
        setSelectedVertical("All");
        setSearchText("");
        setDebouncedSearch("");
        setShowFilters(false);
        setCurrentParams({ page: 1 });
    }, [activeTab]);


    const applyFilters = async (params: Record<string, any>) => {
        setPage(1);
        const newParams = {
            ...params,
            page: 1,
            page_size: pageSize,
            verticals: selectedVertical === "All" ? [] : [selectedVertical],
            category: searchText
        };
        setCurrentParams(newParams);
        setShowFilters(false);
    };

    const applyFiltersBar = async (params: Filters, vertical?: string) => {
        setPage(1);

        const newParams = {
            ...params,
            page: 1,
            page_size: pageSize,
            verticals: vertical === "All" ? [] : [vertical ?? selectedVertical],
            category: searchText
        };

        setCurrentParams(newParams);
        setShowFilters(false);
    };
    const handleView = (offer: Offer) => navigate(`/offers/${offer.id}`);

    const handleToggleSave = async (offerId: number) => {
        if (mode !== "user") return;
        try {
            if (savedOfferIds.includes(offerId)) {
                await deleteSave(offerId);
                setSavedOfferIds((prev) => prev.filter((id) => id !== offerId));
            } else {
                await createSave({ offer_id: offerId });
                setSavedOfferIds((prev) => [...prev, offerId]);
            }
        } catch (e: any) {
            setErr(e?.message || "Failed to update saved offers.");
        }
    };
    const hasActiveFilters =
        (currentFilters.campaign_types?.length ?? 0) > 0 ||
        !!currentFilters.payout_type;

    return (
        <div className="bg-[#F3F4F6] min-h-[100vh] flex flex-col">
            <div className="py-2 px-2 w-full max-w-[1400px] mx-auto min-h-screen">
                {/* Header */}
                <div className="flex flex-col gap-4 mb-4">
                    <div className="flex items-center gap-2">
                        <MdLocalOffer size={32} className="text-gray-500" />
                        <h1 className="text-base sm:text-2xl font-semibold text-gray-600 leading-snug font-sans">
                            Offers
                        </h1>
                    </div>
                </div>

                {/* Tabs + Add Button */}
                <div className="w-full flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                    <div className="flex flex-nowrap items-center gap-2 
                    text-xs sm:text-sm md:text-base
                    w-max">
                        {TABS.map((tab) => (
                            <button
                                key={tab.key}
                                onClick={() => setActiveTab(tab.key)}
                                className={`
        flex items-center justify-center 
        px-2 py-1 sm:px-3 sm:py-1.5 md:px-4 md:py-2
        font-semibold rounded transition
        text-xs sm:text-sm md:text-base
        ${activeTab === tab.key
                                        ? "border-b-4 border-[#F97316] text-[#F97316] bg-white shadow"
                                        : "text-[#606060] hover:text-[#F97316] bg-transparent"
                                    }
    `}
                                style={{ minWidth: 90 }}
                            >
                                {tab.icon}
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    <button
                        onClick={() => setCreateFormOpen(true)}
                        className="w-full md:w-auto flex items-center px-5 py-2.5 rounded-xl shadow 
               font-semibold text-sm border bg-[#F97316] text-white 
               hover:bg-orange-600 transition-colors
               mt-2 md:mt-0"
                    >
                        {mode === "user" ? "+ Add Supply" : "+ Add Offer"}
                    </button>

                </div>

                {/* Filter Bar */}
                <div
                    className="
      flex flex-col md:flex-row 
      items-stretch md:items-center 
      justify-between gap-3 md:gap-4 
      rounded-xl shadow bg-gray-100 
      p-3 md:p-4 mb-4
    "
                >
                    <div className="flex flex-col md:flex-row items-start md:items-center gap-3 w-full">

                        {/* Scrollable horizontal filter bar */}
                        <div className="flex overflow-x-auto scrollbar-hide w-full md:w-auto">
                            <FilterBar
                                options={["All", "Insurance", "Home Services", "Legal", "Debt", "Travel", "Other"]}
                                currentFilter={selectedVertical}
                                onChange={(newValue) => {
                                    setSelectedVertical(newValue);
                                    const newFilters: Filters = {
                                        verticals: newValue === "All" ? [] : [newValue],
                                        payoutRange: { min: "", max: "" },
                                        payout_type: "",
                                        state_list: [],
                                        campaign_types: [],
                                    };
                                    setSearchText("");
                                    setDebouncedSearch("");
                                    setCurrentFilters(newFilters);
                                    applyFiltersBar(newFilters, newValue);
                                }}
                            />
                        </div>
                        <div className="flex-1">
                            <input
                                type="text"
                                placeholder="Search..."
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                className="w-full px-3 py-2 border rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                            />
                        </div>
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className={`flex items-center justify-center 
            px-4 md:px-6 py-2.5 md:py-3 rounded-xl shadow font-semibold text-sm 
            border transition-colors w-full md:w-auto
            ${hasActiveFilters
                                    ? "bg-[#F97316] text-white border-[#F97316]"
                                    : "bg-white text-[#606060] border-[#ececec] hover:bg-gray-50"
                                }`}
                        >
                            <MdFilterList className="mr-2 text-lg" />
                            Filter
                        </button>

                    </div>
                </div>

                {showFilters && (
                    <div className="relative z-10 flex justify-end mb-4">
                        <OfferFilters
                            value={currentFilters}
                            onChange={setCurrentFilters}
                            onApply={applyFilters}
                            onClose={() => setShowFilters(false)}
                        />
                    </div>
                )}

                {/* Offers Grid */}
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {loading &&
                        [...Array(8)].map((_, i) => (
                            <div key={i} className="h-[400px] bg-white rounded-2xl shadow animate-pulse" />
                        ))}

                    {!loading && err && (
                        <div className="col-span-full bg-white rounded-2xl shadow p-6 text-center text-red-600 font-semibold">
                            {err}
                        </div>
                    )}

                    {!loading && !err && filteredOffers.length === 0 && (
                        <div className="col-span-full bg-white rounded-2xl shadow p-10 text-center text-[#606060]">
                            {hasActiveFilters ? "No offers match the selected filters." : "No offers found for this tab."}
                        </div>
                    )}

                    {!loading &&
                        !err &&
                        filteredOffers.map((offer, i) =>
                            mode === "user" ? (
                                <OfferCardAll
                                    key={offer.id ?? i}
                                    offer={offer}
                                    onView={handleView}
                                    isSaved={savedOfferIds.includes(offer.id)}
                                    onToggleSave={handleToggleSave}
                                />
                            ) : (
                                <OfferCardAll key={offer.id ?? i} offer={offer} onView={handleView} isAdmin />
                            )
                        )}
                </div>

                {/* Pagination */}
                <div className="flex justify-center mt-2 gap-2">
                    <button
                        onClick={() => {
                            setPage(Math.max(page - 1, 1))
                            setCurrentParams({ ...currentParams, page: page - 1 });

                        }}
                        disabled={page === 1 || loading}
                        className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
                    >
                        Previous
                    </button>
                    <span className="px-4 py-2">Page {page}</span>
                    <button
                        onClick={() => {
                            setPage(page + 1);
                            setCurrentParams({ ...currentParams, page: page + 1 });
                        }
                        }

                        disabled={loading || filteredOffers.length < pageSize}
                        className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
                    >
                        Next
                    </button>
                </div>
            </div>

            {
                createFormOpen && (
                    <OfferCreateForm
                        onClose={() => setCreateFormOpen(false)}
                        onCreated={() => {
                            fetchOffers(currentParams);
                        }}
                        isUser={mode === "user"}
                    />
                )
            }
        </div >
    );
}
